import React, { useCallback, useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { PrismicRichText } from "@prismicio/react";
import { PrismicNextLink } from "@prismicio/next";
import Quote from "@/app/assets/Quote.svg";
import Image from "next/image";

const EmblaCarousel = ({ list = [] }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start" }, []);

  const onNavButtonClick = useCallback((emblaApi) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  return (
    <div className="embla overflow-hidden" ref={emblaRef}>
      <div className="embla__container">
        {list?.map((item, idx) => {
          return (
            <div key={idx} className={"embla__slide"}>
              <div className="md:flex gap-x-[20px] items-start text-[#fff]">
                <Image src={Quote} alt="Quote" width={43} height={33} />
                <div className={"mt-5 md:mt-0"}>
                  <div className="mb-[22px]">
                    <PrismicRichText
                      field={item?.testimony}
                      components={bodyComponent}
                    />
                  </div>
                  <p className="text-base">-By {item?.name}</p>
                </div>
              </div>
            </div>
          );
        })}{" "}
      </div>

      <div className="embla__controls">
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={"embla__dot".concat(
                index === selectedIndex ? " embla__dot--selected" : "",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmblaCarousel;

const useDotButton = (emblaApi, onButtonClick) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onDotButtonClick = useCallback(
    (index) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      if (onButtonClick) onButtonClick(emblaApi);
    },
    [emblaApi, onButtonClick],
  );

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

export const DotButton = (props) => {
  const { children, ...restProps } = props;

  return (
    <button type="button" {...restProps} aria-label="Dot navigation">
      {children}
    </button>
  );
};

const bodyComponent = {
  paragraph: ({ children }) => <p className="text-base">"{children}"</p>,
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  hyperlink: ({ children, node }) => (
    <PrismicNextLink
      field={node.data}
      className="decoration-1 underline-offset-2 text-base"
    >
      {children}
    </PrismicNextLink>
  ),
};
